import React from 'react';
import { X, Power, AlertTriangle, CheckCircle } from 'lucide-react';

const ToggleSubAccountStatusModal = ({ 
  isOpen, 
  onClose, 
  selectedSubAccount, 
  onConfirm, 
  isProcessing,
  getSubAccountDisplayName 
}) => {
  if (!isOpen || !selectedSubAccount) return null;

  const isActive = selectedSubAccount.isActive;
  const actionLabel = isActive ? 'Deactivate' : 'Activate';
  const actionLabelLower = isActive ? 'deactivate' : 'activate';

  const handleConfirm = () => {
    onConfirm(selectedSubAccount._id, isActive);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md mx-4 bg-white rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-light-gray">
          <div className="flex items-center">
            <Power className={`h-6 w-6 mr-3 ${isActive ? 'text-error-600' : 'text-primary-600'}`} />
            <h3 className="text-lg font-medium text-secondary-900">
              {actionLabel} Sub Account
            </h3>
          </div>
          <button
            onClick={onClose}
            className="transition-colors text-secondary-400 hover:text-secondary-600"
            disabled={isProcessing}
            style={{ color: '#9ca3af' }}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className={`h-16 w-16 rounded-full flex items-center justify-center ${
              isActive ? 'bg-error-100' : 'bg-primary-100'
            }`}>
              {isActive ? (
                <AlertTriangle className="w-8 h-8 text-error-600" />
              ) : (
                <CheckCircle className="w-8 h-8 text-primary-600" />
              )}
            </div>
          </div>

          {/* Message */}
          <div className="mb-6 text-center">
            <h4 className="mb-2 text-lg font-medium text-secondary-900">
              Are you sure you want to {actionLabelLower} this sub-account?
            </h4>
            <p className="text-sm text-secondary-600">
              {isActive 
                ? 'The sub-account will no longer be able to access the system until reactivated'
                : 'The sub-account will regain access to the system with their assigned permissions'}
            </p>
          </div>

          {/* Sub Account Details */}
          <div className="p-4 mb-6 rounded-lg bg-secondary-50">
            <div className="flex items-center">
              <div className="flex-shrink-0 w-10 h-10">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-100">
                  <span className="text-sm font-medium text-primary-600">
                    {getSubAccountDisplayName(selectedSubAccount).charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <div className="text-sm font-medium text-secondary-900">
                  {getSubAccountDisplayName(selectedSubAccount)}
                </div>
                <div className="text-sm text-secondary-500">
                  {selectedSubAccount.email}
                </div>
                <div className="flex items-center mt-1">
                 
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    isActive ? 'bg-success-100 text-success-800' : 'bg-error-100 text-error-800'
                  }`}>
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className={`border rounded-md p-4 mb-6 ${
            isActive 
              ? 'bg-error-50 border-error-200' 
              : 'bg-info-50 border-info-200'
          }`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {isActive ? (
                  <AlertTriangle className="w-5 h-5 text-error-400" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-primary-400" />
                )}
              </div>
              <div className="ml-3">
                <h3 className={`text-sm font-medium ${
                  isActive ? 'text-error-800' : 'text-primary-800'
                }`}>
                  {isActive ? 'What happens when you deactivate?' : 'What happens when you activate?'}
                </h3>
                <div className={`mt-2 text-sm ${
                  isActive ? 'text-error-700' : 'text-primary-700'
                }`}>
                  {isActive ? (
                    <>
                      <p>• They will not be able to log in until reactivated</p>
                      <p>• All their data and permissions will be preserved</p>
                    </>
                  ) : (
                    <>
                      <p>• The sub-account will be able to log in again</p>
                      <p>• Their previous permissions will be restored</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isProcessing}
              className={isActive ? 'btn-error' : 'btn-primary'}
            >
              {isProcessing ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></div>
                  Processing...
                </div>
              ) : (
                `${actionLabel} Sub Account`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToggleSubAccountStatusModal;

